import React, { FocusEvent, memo, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import orderBy from 'lodash/orderBy'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { getRecentFacilities } from 'apps/fleet-planner/utils/actions/facilities'
import { IFacility } from 'apps/fleet-planner/utils/api'
import { selectRecentFacilities } from 'apps/fleet-planner/utils/selectors'
import { getEntityById } from 'utils/getEntityById'

interface IGetLabelFromLocationArgs {
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  postalCode?: string
}

/**
 * Generates option label from location values,
 * which may be defined or undefined
 */
export const getLabelFromLocation = ({
  addressLine1 = '',
  addressLine2 = '',
  city = '',
  postalCode = '',
  state = '',
}: IGetLabelFromLocationArgs): string => {
  let addressString = ''
  if (addressLine1) addressString += ` ${addressLine1},`
  if (addressLine2) addressString += ` ${addressLine2},`
  if (city) addressString += ` ${city},`
  if (state) addressString += ` ${state}`
  if (postalCode) addressString += ` ${postalCode}`
  return addressString.trim()
}

/**
 * Generates option label with facility name
 */
export const getFacilityLabelFromLocation = (
  name: string = '',
  location: IGetLabelFromLocationArgs = {}
): string => {
  let addressString = ' · '
  addressString += getLabelFromLocation(location)
  return `${name.trim()}${addressString}`
}

interface IFacilityAutosuggestProps extends IAutosuggestWrapperProps<IFacility> {}

export const FacilityAutosuggest = memo<IFacilityAutosuggestProps>(
  ({
    dataTest,
    disabled: disabledProp,
    error: errorProp,
    fetchList = true,
    icon: iconProp = 'search',
    label: labelProp = 'Facility',
    large: largeProp = true,
    onAddEntity: onAddEntityProp,
    onBlur: onBlurProp,
    onCancel: onCancelProp,
    onChange: onChangeProp,
    onInputChange: onInputChangeProp,
    placeholder: placeholderProp = 'Search by facility location or name',
    required,
    value: valueProp,
  }) => {
    const dispatch = useDispatch()
    const facilities = useSelector(selectRecentFacilities)
    const { isMobile } = useBreakpointContext()

    const [inputValue, setInputValue] = useState('')

    // If fetchList is true, do this on mount
    useEffect(() => {
      if (fetchList && !facilities?.length) dispatch(getRecentFacilities({ pageSize: 1000 }))
    }, [fetchList, dispatch, facilities?.length])

    const changeHandler = (option: IAutocompleteOption | undefined) => {
      const newFacility = getEntityById(facilities, option?.id)
      onChangeProp(newFacility)
    }

    const options = getAutosuggestOptions({
      options: orderBy(facilities, 'id', ['desc']),
      groupIndex: 1,
      groupName: inputValue ? 'Facility' : 'Recent facilities',
      entityName: 'New Facility',
      iconName: 'LocationPinIcon',
      inputValue,
      mapOption: ({ addresses, id, name }: IFacility) => {
        const [address] = addresses || []
        const { addressLine1 = '', locality = '', postalCode = '', subdivisionCode = '' } =
          address || {}
        const text = getFacilityLabelFromLocation(name, {
          city: locality,
          state: subdivisionCode,
          postalCode: postalCode ?? '',
          addressLine1,
        })
        return {
          id,
          text,
        }
      },
    })

    const inputChangeHandler = (val: string = '') => {
      setInputValue(val)
      if (onInputChangeProp) onInputChangeProp(val)
    }

    const blurHandler = (e: FocusEvent<HTMLInputElement>) => {
      if (onBlurProp) onBlurProp(e)
    }

    const value = getEntityById(facilities, valueProp?.id)

    return (
      <Autosuggest
        dataTest={dataTest}
        aria-label={`Facility autosuggest ${valueProp?.id}`}
        large={largeProp}
        label={labelProp}
        placeholder={placeholderProp}
        icon={iconProp}
        renderOptionType='withIcon'
        value={value}
        options={options}
        onChange={changeHandler}
        onInputChange={inputChangeHandler}
        onAddEntity={onAddEntityProp}
        onCancel={onCancelProp}
        clearable
        onBlur={blurHandler}
        error={errorProp}
        required={required}
        disabled={disabledProp}
        selectOnFocus={!isMobile}
      />
    )
  }
)
